import { useEffect, useRef, useState } from 'react';
import { Icon, cardClasses, cn, type IconName } from '@evogard/ui';
import { ASSISTANCE_SERVICES, type ManagementStage } from '@evogard/domain';
import type { ManagementDispatch, ManagementOverview } from '@evogard/contract';
import { STAGE_COLOR, STAGE_LABEL } from './ManagementMap.js';

/**
 * O modo ao vivo da Gestão da assistência (30/10/2026): a tela relê a cada
 * `LIVE_INTERVAL_MS`, compara a leitura nova com a anterior e AVISA dentro do
 * mapa o que mudou — o serviço que entrou e a etapa que andou —, com o pino
 * pulsando por `FRESH_MS`.
 *
 * ## Por que é seguro deixar ligado
 *
 * - **Um pedido por vez:** o React Query não dispara a releitura seguinte
 *   enquanto a anterior está no ar, então uma resposta lenta não empilha.
 * - **Aba escondida não consulta:** o intervalo para quando a aba sai de
 *   vista (`refetchIntervalInBackground` falso, o padrão) e volta sozinho.
 * - **Desliga sozinho** depois de `LIVE_MAX_MS`: a aba esquecida numa TV não
 *   fica consultando a madrugada inteira. Religar é um clique.
 * - **Só leitura:** é a mesma rota da tela, sob a mesma permissão; nada é
 *   gravado.
 *
 * A comparação é por acionamento: `id` que não existia é serviço novo, e
 * `stage` diferente é etapa que mudou. Trocar o período ou a praça recomeça a
 * comparação — senão os 30 dias inteiros chegariam como "novos".
 */

export const LIVE_INTERVAL_MS = 5_000;
export const LIVE_MAX_MS = 30 * 60_000;
/** Quanto tempo o pino pulsa e o aviso fica na tela. */
const FRESH_MS = 15_000;
/** Quantos avisos cabem na pilha; o mais velho sai primeiro. */
const MAX_NOTICES = 4;

/**
 * O ícone de cada etapa, na pastilha do cartão, no aviso e no card. A cor é a
 * do pino (`STAGE_COLOR`); o ícone diz a etapa para quem não distingue as
 * cores.
 */
// @valor
export const STAGE_ICON: Record<ManagementStage, IconName> = {
  nao_abriu: 'bloqueado',
  link_aberto: 'olho',
  a_caminho: 'veiculo',
  no_local: 'mapa',
  em_servico: 'atualizar',
  em_transito: 'assistencia',
  no_destino: 'bandeira',
  finalizado: 'confirmado',
  agendado: 'calendario',
  cancelado: 'fechar',
};

/** O ícone dentro de uma pastilha tingida com a cor da etapa. */
export function StageMark({
  stage,
  size = 'md',
}: {
  stage: ManagementStage;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center rounded-lg',
        size === 'md' ? 'size-8' : 'size-6',
      )}
      style={{
        color: STAGE_COLOR[stage],
        background: `color-mix(in oklch, ${STAGE_COLOR[stage]} 14%, transparent)`,
      }}
    >
      <Icon nome={STAGE_ICON[stage]} size={16} />
    </span>
  );
}

export interface LiveNotice {
  key: string;
  id: number;
  code: string;
  serviceType: ManagementDispatch['serviceType'];
  kind: 'novo' | 'etapa';
  from: ManagementStage | null;
  to: ManagementStage;
  at: number;
}

const SERVICE_LABEL = Object.fromEntries(
  ASSISTANCE_SERVICES.map((s) => [s.type, s.label]),
) as Record<ManagementDispatch['serviceType'], string>;

/**
 * Compara cada leitura com a anterior e devolve os avisos e os pinos que
 * acabaram de mudar. `scope` é o que, mudando, recomeça a comparação.
 */
export function useLiveFeed(
  data: ManagementOverview | undefined,
  enabled: boolean,
  scope: string,
) {
  const previous = useRef<{ scope: string; stages: Map<number, ManagementStage> } | null>(
    null,
  );
  const [notices, setNotices] = useState<LiveNotice[]>([]);
  const [fresh, setFresh] = useState<ReadonlyMap<number, number>>(new Map());

  useEffect(() => {
    if (!data) return;
    const stages = new Map(data.items.map((d) => [d.id, d.stage]));
    const before = previous.current;
    previous.current = { scope, stages };
    if (!enabled || !before || before.scope !== scope) return;

    const now = Date.now();
    const found: LiveNotice[] = [];
    for (const d of data.items) {
      const was = before.stages.get(d.id);
      if (was === d.stage) continue;
      found.push({
        key: `${d.id}:${d.stage}:${now}`,
        id: d.id,
        code: d.code,
        serviceType: d.serviceType,
        kind: was === undefined ? 'novo' : 'etapa',
        from: was ?? null,
        to: d.stage,
        at: now,
      });
    }
    if (found.length === 0) return;
    setNotices((list) => [...found, ...list].slice(0, MAX_NOTICES));
    setFresh((m) => {
      const next = new Map(m);
      for (const n of found) next.set(n.id, now + FRESH_MS);
      return next;
    });
  }, [data, enabled, scope]);

  /* O aviso e o pulso vencem sozinhos. */
  useEffect(() => {
    if (notices.length === 0 && fresh.size === 0) return;
    const t = setInterval(() => {
      const now = Date.now();
      setNotices((list) => {
        const kept = list.filter((n) => now - n.at < FRESH_MS);
        return kept.length === list.length ? list : kept;
      });
      setFresh((m) => {
        if ([...m.values()].every((until) => until > now)) return m;
        return new Map([...m].filter(([, until]) => until > now));
      });
    }, 1_000);
    return () => clearInterval(t);
  }, [notices.length, fresh.size]);

  /* Desligar o ao vivo limpa o que ficou. */
  useEffect(() => {
    if (enabled) return;
    setNotices([]);
    setFresh(new Map());
  }, [enabled]);

  const dismiss = (key: string) =>
    setNotices((list) => list.filter((n) => n.key !== key));
  return { notices, fresh: new Set(fresh.keys()), dismiss };
}

/** Os avisos dentro do mapa, no canto de baixo à direita; o clique escolhe o serviço. */
export function LiveNotices({
  notices,
  onPick,
  onDismiss,
}: {
  notices: readonly LiveNotice[];
  onPick: (id: number) => void;
  onDismiss: (key: string) => void;
}) {
  return (
    <ol
      aria-live="polite"
      aria-label="O que acabou de mudar"
      className="flex flex-col gap-2"
    >
      {notices.map((n) => (
        <li
          key={n.key}
          className={cn(
            cardClasses({ padding: false }),
            'flex animate-in items-start gap-2.5 p-2.5 shadow-lg fade-in-0 slide-in-from-bottom-2',
          )}
          style={{ borderColor: STAGE_COLOR[n.to] }}
        >
          <StageMark stage={n.to} />
          <button
            type="button"
            onClick={() => onPick(n.id)}
            className="min-w-0 flex-1 text-left text-xs"
          >
            <b className="block text-sm text-texto">
              {n.kind === 'novo' ? 'Novo acionamento' : 'Mudou de etapa'}
            </b>
            <span className="block text-texto-suave">
              <span className="font-mono">{n.code}</span> · {SERVICE_LABEL[n.serviceType]}
            </span>
            <span className="block font-semibold" style={{ color: STAGE_COLOR[n.to] }}>
              {n.from
                ? `${STAGE_LABEL[n.from]} → ${STAGE_LABEL[n.to]}`
                : STAGE_LABEL[n.to]}
            </span>
          </button>
          <button
            type="button"
            onClick={() => onDismiss(n.key)}
            aria-label="Dispensar o aviso"
            className="grid size-7 place-items-center rounded-lg text-texto-tenue hover:bg-superficie-suave"
          >
            <Icon nome="fechar" size={16} />
          </button>
        </li>
      ))}
    </ol>
  );
}
